// Limen mellem spillet og Volt (M3): opretter controlleren og panelet, oversætter sim-hændelser til
// Volts replikker og kortforslag, holder øje med spillerens cursor og skriver logbogen på
// resultatskærmen. main.ts kalder kun en håndfuld metoder herfra, så fletningen med andre spor er let.
//
// Volt er holdkammerat med roller (spor "sandhed"): spilleren vælger Planlægger eller Driftsvagt, og
// Volt tager den anden rolle med samme regler som en makker online. Hans handlinger går gennem
// game.pushAs(volt, ...) med kilden 'jev' (eller 'bot', når rygmarven valgte), aldrig gennem
// game.brain, som er autoplay for den, der sidder ved skærmen.

import type { Atlas } from '../render/atlas';
import type { P } from '../render/geo';
import type { Scene } from '../render/scene';
import type { Game } from '../game';
import { buildCandidates, cardCandidates, labelOf } from '../ai/candidates';
import type { VoltCandidate } from '../ai/candidates';
import { makeTransport } from '../ai/client';
import { adviceIntent, cityAdvice, isPlant } from '../ai/coach';
import type { Transport } from '../ai/client';
import { VoltController } from '../ai/controller';
import type { VoltStats } from '../ai/controller';
import type { LineEvent } from '../ai/protocol';
import { check } from '../sim';
import type { ActionKind, ApplyError, Candidate, CardOption, PlayerId, SimEvent, State } from '../sim';
import { nodeById } from '../sim/grid';
import { roleBlocked } from '../sim/roles';
import { cardOpen, isCard } from '../sim/threats';
import { CARD_OPTION } from './cards';
import type { CardPanel } from './cards';
import type { Toast } from './toast';
import { VoltPanel, logbookEl } from './volt-panel';
import type { VoltMoments } from './volt-panel';

export type VoltMode = 'volt' | 'bot';
/** Spillerens rolle, når Volt spiller med. Volt tager den anden. */
export type SoloRole = 'planner' | 'drift';

export interface VoltSessionDeps {
  game: Game;
  scene: Pick<Scene, 'nodeScreen' | 'lineScreen'>;
  atlas: Pick<Atlas, 'image'>;
  cards: Pick<CardPanel, 'suggest'>;
  toast: Pick<Toast, 'show'>;
  stage: HTMLElement;
  root: HTMLElement;
  agent: 'mock' | 'live';
  /** Pak transporten ind (dev-fixturerne bytter Volts replikker ved grænsen). */
  wrap?: ((t: Transport) => Transport) | undefined;
  /** Står en skærm foran spillet? */
  covered(): boolean;
  /** Er det spillerens egen bane (ikke titlens baggrund)? */
  live(): boolean;
  /** Skal Volt vente på spilleren (første ledning er ikke lært endnu)? Replikken, eller null. */
  hold?(): string | null;
  /** Det, vejledningen peger spilleren hen til lige nu: dem lader Volt være. */
  reserved?(): readonly string[];
}

/** Cursoren skal være så tæt på (px), før Volt lader målet være. */
const NEAR_PX = 72;
/** Så længe huskes cursoren (ms). */
const NEAR_MS = 2000;
/** Kortet er Volts job: så længe står hans bud på kortet, før han svarer (ms), så spilleren ser det. */
const CARD_SHOW_MS = 1400;
/** Kommer Volts bud ikke (Jev svarer langsomt), svarer han selv på rygmarv efter så lang tid (ms). */
const CARD_FALLBACK_MS = 3200;

/** Volts korte grund, når han siger nej til et spørgsmål. */
const WHY_NOT: Partial<Record<ApplyError | 'paused', string>> = {
  too_poor: 'kassen er for tom',
  no_slots: 'der ikke er flere pladser',
  paused: 'uret står stille',
  not_needed: 'der ikke er brug for det lige nu',
  busy: 'der allerede bliver arbejdet på den',
  duplicate: 'det allerede er sat i gang',
  no_crew: 'alle montører har travlt',
  too_late: 'stormen allerede er her',
  too_far: 'der er for langt',
  not_allowed: 'det ikke kan lade sig gøre her',
};

export class VoltSession {
  readonly panel: VoltPanel;
  private mode: VoltMode | null = null;
  private role: SoloRole = 'planner';
  private controller: VoltController | null = null;
  private transports = new Map<string, Transport>();
  private trail: { x: number; y: number; t: number }[] = [];
  private faceUrl = '';
  /** Det Volt selv nåede på banen, til logbogen. */
  private moments: VoltMoments = { built: 0, fixed: 0, best: null, final: null };
  /** Bedste øjebliks vægt (redning i storm > jaget gravko > reparation > første ledning). */
  private bestRank = 0;
  /** Ledninger, hvor Volt forstærkede masterne (så en redning i stormen er hans). */
  private reinforced = new Set<string>();
  /** Logbogen, der står på resultatskærmen lige nu (den sidste replik kan komme lidt efter). */
  private book: HTMLElement | null = null;
  /**
   * Et kort, der er Volts job (han er Driftsvagt): han svarer selv, ellers står uret stille bag kortet
   * for evigt (IGN-løkke 2, B2). Buddet vises først, så spilleren kan se, hvad han vælger.
   */
  private duty: { threat: string; opt: CardOption | null; due: number } | null = null;

  constructor(private deps: VoltSessionDeps) {
    const img = (n: string): string => deps.atlas.image(n);
    this.faceUrl = img('volt');
    // Knappen "Spil med Volt" bruger samme ansigt som panelet.
    if (this.faceUrl) document.documentElement.style.setProperty('--volt-face', `url("${img('volt_face')}")`);
    this.panel = new VoltPanel(
      { volt: this.faceUrl, think: img('volt_think'), worried: img('volt_worried'), blink: img('volt_blink'), marker: img('volt_marker') },
      {
        claim: () => this.controller?.claim(),
        answer: (id) => this.controller?.answer(id),
        instruct: (text, chip) => this.controller?.instruct(text, chip),
        unset: (what) => this.controller?.unset(what),
      },
    );
    deps.root.append(this.panel.el, this.panel.marker);
    // Tankepanelet starter foldet sammen overalt: ansigt og taleboble er nok til at følge Volt, og
    // knapper, tanker og log fylder først, når man selv trykker "Tanker".
    this.panel.setOpen(false);
    const note = (e: PointerEvent): void => {
      const r = deps.stage.getBoundingClientRect();
      const t = performance.now();
      this.trail.push({ x: e.clientX - r.left, y: e.clientY - r.top, t });
      while (this.trail.length > 0 && (this.trail[0]?.t ?? t) < t - NEAR_MS) this.trail.shift();
      if (this.trail.length > 120) this.trail.splice(0, this.trail.length - 120);
    };
    deps.stage.addEventListener('pointerdown', note, { passive: true, capture: true });
    deps.stage.addEventListener('pointermove', note, { passive: true, capture: true });
  }

  /** Spiller Volt med? */
  get on(): boolean {
    return this.mode !== null;
  }

  get current(): VoltMode | null {
    return this.mode;
  }

  /** Slå Volt til ('volt' med Jev og Haiku, 'bot' kun rygmarv) eller fra. Gælder fra næste bane. */
  set(mode: VoltMode | null): void {
    this.mode = mode;
    if (!mode) {
      this.controller = null;
      this.panel.show(false);
    }
  }

  /** Spillerens rolle (Volt tager den anden). Gælder fra næste begin(). */
  setRole(role: SoloRole): void {
    this.role = role;
  }

  get playerRole(): SoloRole {
    return this.role;
  }

  /** Spillerens plads: Planlæggeren er p1, Driftsvagten p2. */
  get seat(): PlayerId {
    return this.role === 'drift' ? 'p2' : 'p1';
  }

  /** Volts plads: den anden. */
  get voltSeat(): PlayerId {
    return this.role === 'drift' ? 'p1' : 'p2';
  }

  /** Valg til game.start: co-op med roller, så Volt og spilleren har hver sit job. */
  opts(): { coop?: boolean; roles?: boolean } {
    return this.mode ? { coop: true, roles: true } : {};
  }

  /** Ny bane er startet (efter game.start). */
  begin(): void {
    this.panel.reset();
    this.trail = [];
    this.moments = { built: 0, fixed: 0, best: null, final: null };
    this.bestRank = 0;
    this.reinforced.clear();
    this.book = null;
    this.duty = null;
    if (!this.mode) {
      this.controller = null;
      return;
    }
    const transport = this.transport(this.mode);
    void transport.warm().catch(() => undefined);
    const { game } = this.deps;
    const now = (): number => performance.now();
    const me = this.voltSeat;
    this.controller = new VoltController({
      transport,
      player: me,
      now,
      state: () => game.state,
      canAct: () => this.canAct(),
      push: (c, by) => {
        const r = game.pushAs(me, c.kind, c.target, c.opt, by);
        return r.ok ? true : r.error;
      },
      nearCursor: (nodes) => this.near(nodes),
      hold: () => this.deps.hold?.() ?? null,
      reserved: () => this.deps.reserved?.() ?? [],
      ui: {
        dropped: () => this.panel.acted(),
        thinking: (on) => this.panel.thinking(on),
        thought: (d) => this.panel.thought(d),
        announce: (c, ms, by) => this.panel.announce(c, ms, by, now(), !roleBlocked(game.state, c.cand.kind, game.player)),
        ask: (top, ms, proposal) => this.panel.ask(top, ms, now(), proposal),
        clearAsk: () => this.panel.clearAsk(),
        say: (line, mood) => this.said(line, mood, now()),
        policy: (p) => this.panel.policy(p),
        brain: (by, ms) => this.panel.brain(by, ms),
        acted: (c) => this.acted(c),
        suggest: (threat, opt, label) => this.suggest(threat, opt, label),
      },
    });
    this.controller.reset();
  }

  private transport(mode: VoltMode): Transport {
    const key = mode === 'bot' ? 'spine' : this.deps.agent;
    let t = this.transports.get(key);
    if (!t) {
      t = makeTransport(key);
      if (this.deps.wrap) t = this.deps.wrap(t);
      this.transports.set(key, t);
    }
    return t;
  }

  private canAct(): boolean {
    const { game } = this.deps;
    return game.running && game.state.phase === 'playing' && !this.deps.covered() && this.deps.live();
  }

  /** Har spilleren haft cursoren tæt på et af stederne de sidste 2 s? */
  private near(nodes: readonly string[]): boolean {
    const t = performance.now() - NEAR_MS;
    const pts = this.trail.filter((p) => p.t >= t);
    if (pts.length === 0) return false;
    for (const id of nodes) {
      const n = this.deps.scene.nodeScreen(id);
      if (!n) continue;
      for (const p of pts) if (Math.hypot(p.x - n.x, p.y - n.y) < NEAR_PX) return true;
    }
    return false;
  }

  /** Volt siger noget. Når banen er slut, er det hans sidste ord, og de kommer med i logbogen. */
  private said(line: string, mood: Parameters<VoltPanel['speak']>[1], now: number): void {
    this.panel.speak(line, mood, now);
    const ph = this.deps.game.state.phase;
    if (ph !== 'won' && ph !== 'lost') return;
    this.moments.final = line;
    const q = this.book?.querySelector<HTMLElement>('.volt-final');
    if (q) {
      q.textContent = line;
      q.hidden = false;
    }
  }

  /** Husk et godt øjeblik, hvis det slår det hidtil bedste. */
  private moment(rank: number, text: string): void {
    if (rank <= this.bestRank) return;
    this.bestRank = rank;
    this.moments.best = text;
  }

  private lineName(id: string): string {
    const s = this.deps.game.state;
    const l = s.lines.find((x) => x.id === id);
    if (!l) return '';
    return `${nodeById(s, l.a)?.name ?? ''} til ${nodeById(s, l.b)?.name ?? ''}`;
  }

  private acted(c: VoltCandidate): void {
    this.panel.acted();
    const k = c.cand.kind;
    const t = c.cand.target ?? '';
    if (k === 'reinforce_mast' && t === '*') {
      for (const th of this.deps.game.state.threats) if (th.kind === 'storm') for (const id of th.atRisk) this.reinforced.add(id);
    } else if (k === 'reinforce_mast') this.reinforced.add(t);
    if (k === 'shoo_threat') {
      this.moments.fixed++;
      const th = this.deps.game.state.threats.find((x) => x.id === t);
      const what = th?.kind === 'anchor' ? 'ankerbåden' : 'gravkoen';
      this.moment(3, `Jeg jog ${what} væk fra ${this.lineName(th?.targetLine ?? '') || 'ledningen'}.`);
    }
    if (k === 'dispatch_crew') {
      this.moments.fixed++;
      this.moment(2, `Jeg sendte et hold ud og lappede ${this.lineName(t) || 'en knækket ledning'}.`);
    }
    const ev: Partial<Record<string, LineEvent>> = { upgrade_line: 'upgraded', reinforce_mast: 'reinforced', dispatch_crew: 'repaired' };
    const e = ev[c.cand.kind];
    if (e) this.controller?.event(e);
  }

  private suggest(threat: string, opt: CardOption | null, label: string): void {
    this.deps.cards.suggest(threat, opt);
    const mine = this.cardIsMine();
    if (opt) this.panel.speak(mine ? `Jeg tager ${label.toLowerCase()}.` : `Mit bud: ${label.toLowerCase()}.`, 'calm', performance.now());
    if (mine && opt && this.duty?.threat === threat) {
      this.duty.opt = opt;
      this.duty.due = Math.min(this.duty.due, performance.now() + CARD_SHOW_MS);
    }
  }

  /** Er kortene Volts job (spilleren er Planlægger)? */
  private cardIsMine(): boolean {
    const { game } = this.deps;
    return this.mode !== null && roleBlocked(game.state, 'answer_card', game.player);
  }

  /** Svar på kortet, når tiden er inde: Volts bud, eller rygmarvens bedste, hvis buddet ikke kom. */
  private doDuty(now: number): void {
    const { game } = this.deps;
    const s = game.state;
    if (s.phase !== 'playing' || game.over) return;
    if (!this.duty && this.cardIsMine()) {
      // Et kort, Volt ikke hørte komme (fx mens spilleren lærte første ledning): han tager det nu.
      const open = s.threats.find(cardOpen);
      if (open) this.duty = { threat: open.id, opt: null, due: now + CARD_FALLBACK_MS };
    }
    const d = this.duty;
    if (!d || now < d.due) return;
    const t = s.threats.find((x) => x.id === d.threat);
    if (!t || !cardOpen(t)) {
      this.duty = null;
      return;
    }
    const ok = (o: CardOption): boolean => check(s, 'answer_card', d.threat, o, this.voltSeat).ok;
    const opt = d.opt && ok(d.opt) ? d.opt : (cardCandidates(s, d.threat).map((c) => c.cand.opt as CardOption).find(ok) ?? null);
    this.duty = null;
    if (!opt) return;
    const r = game.pushAs(this.voltSeat, 'answer_card', d.threat, opt, d.opt ? 'jev' : 'bot');
    if (r.ok) this.deps.toast.show(`Volt valgte ${CARD_OPTION[opt].label.toLowerCase()}.`, 'info', 2200);
  }

  /** Hver frame. */
  frame(now: number): void {
    const show = this.mode !== null && this.deps.live() && !this.deps.covered();
    this.panel.show(show);
    if (!show || !this.controller) return;
    this.doDuty(now);
    this.controller.update();
    this.panel.plan(this.controller.teamPlan?.note || null);
    this.panel.update(now, this.markerAt());
    if (now - this.tuckAt > 250) {
      this.tuckAt = now;
      this.panel.tuck(this.coversTrouble());
    }
    if (now - this.prunedAt > 400) {
      this.prunedAt = now;
      const s = this.deps.game.state;
      this.panel.prune((id) => {
        const [kind, target, opt] = id.split(':');
        return check(s, kind as ActionKind, target || undefined, opt || undefined, this.voltSeat).ok;
      });
    }
  }

  private prunedAt = 0;
  private tuckAt = 0;
  /** Panelets fulde ramme, målt mens det var foldet ud: så blinker det ikke, når det folder sammen. */
  private fullRect: DOMRect | null = null;

  /** Står en by med kørende mørkeur (eller i sort) bag Volts panel? */
  private coversTrouble(): boolean {
    const el = this.panel.el;
    if (!this.panel.tucked) this.fullRect = el.getBoundingClientRect();
    const r = this.fullRect;
    if (!r || r.width === 0) return false;
    const o = this.deps.root.getBoundingClientRect();
    const pad = 16;
    for (const n of this.deps.game.state.nodes) {
      if (!n.active || n.phantom || !n.city || !(n.city.out || n.city.clock > 0)) continue;
      const p = this.deps.scene.nodeScreen(n.id);
      if (!p) continue;
      const x = p.x + o.left;
      const y = p.y + o.top;
      if (x > r.left - pad && x < r.right + pad && y > r.top - pad && y < r.bottom + pad) return true;
    }
    return false;
  }

  /** Hvor markøren skal stå: midt på det Volt er ved at gøre, i #app's koordinater. */
  private markerAt(): P | null {
    const c = this.controller?.intent;
    if (!c) return null;
    const { scene } = this.deps;
    const pts = c.nodes.map((id) => scene.nodeScreen(id)).filter((p): p is P => !!p);
    let at: P | null = null;
    if (pts.length > 0) at = { x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length };
    else if (c.cand.target) at = scene.lineScreen(c.cand.target);
    if (!at) return null;
    const sr = this.deps.stage.getBoundingClientRect();
    const rr = this.deps.root.getBoundingClientRect();
    return { x: at.x + sr.left - rr.left, y: at.y + sr.top - rr.top };
  }

  /** Sim-hændelser: replikker, kortforslag og "Volt byggede ...". */
  events(evs: SimEvent[], s: State): void {
    const c = this.controller;
    if (!c || !this.deps.live()) return;
    const name = (id: string): string => nodeById(s, id)?.name ?? '';
    // Mens spilleren lærer (første ledning, øvelsesstormen), er Volt tavs: ingen replikker på hændelser.
    if (this.deps.hold?.()) {
      if (evs.some((e) => e.t === 'level_won' || e.t === 'level_lost')) c.event(evs.some((e) => e.t === 'level_won') ? 'won' : 'lost');
      return;
    }
    for (const e of evs) {
      switch (e.t) {
        case 'line_built': {
          if (e.by === this.voltSeat) {
            const l = s.lines.find((x) => x.id === e.line);
            if (l) this.deps.toast.show(`Volt byggede ${name(l.a)} til ${name(l.b)}.`, 'info', 2200, this.deps.scene.lineScreen(e.line));
            this.moments.built++;
            // Et kulværk er ikke et højdepunkt (IGN runde 1: "Bedste øjeblik" var en ledning til kul).
            const plant = l && [l.a, l.b].some((id) => { const n = nodeById(s, id); return !!n && isPlant(n); });
            // En by, hvis mørkeur løb, er et rigtigt højdepunkt.
            const saved = l ? [l.a, l.b].map((id) => nodeById(s, id)).find((n) => n?.city && (n.city.out || n.city.clock > 0)) : undefined;
            if (saved) this.moment(3, `Jeg gav ${saved.name} strøm, mens uret løb.`);
            if (l && !plant) this.moment(1, `Min første ledning, ${name(l.a)} til ${name(l.b)}.`);
            c.event('built');
          } else c.event('player_built');
          break;
        }
        case 'line_saved':
          if (this.reinforced.has(e.line)) {
            this.moments.fixed++;
            this.moment(4, `Mine master holdt ${this.lineName(e.line)} gennem stormen.`);
          }
          c.event('saved');
          break;
        case 'line_repaired':
          c.event('repaired');
          break;
        case 'blackout':
          c.event('blackout', name(e.node));
          break;
        case 'warning':
          if (e.kind === 'storm') c.event('storm');
          else if (isCard(e.kind)) {
            if (this.cardIsMine()) this.duty = { threat: e.threat, opt: null, due: performance.now() + CARD_FALLBACK_MS };
            c.card(e.threat);
          }
          break;
        case 'year_end':
          c.event('year');
          break;
        case 'level_won':
          c.event('won');
          break;
        case 'level_lost':
          c.event('lost');
          break;
        default:
          break;
      }
    }
  }

  /**
   * IGN-løkke 2, runde 3 (co-op): spilleren spørger Volt om et træk, der er hans job ("Forstærk?",
   * "Byg her?"). Før kunne Volt kun spørges online. Han svarer med det samme og synligt: enten gør han
   * det ("Jeg tager den: ..."), eller han siger ærligt hvorfor ikke.
   */
  ask(at: string, kind: ActionKind): boolean {
    if (!this.mode || !this.controller) return false;
    const { game } = this.deps;
    const s = game.state;
    const me = this.voltSeat;
    let move: Candidate | null = { id: 'ask', kind, target: at, cost: 0 };
    if (kind === 'build_line') {
      // "Byg her?" på en by: det træk, byens kort og hintet siger, ellers Volts bedste ledning derfra.
      const a = cityAdvice(s, at);
      const i = a ? adviceIntent(s, a, me) : null;
      if (i?.kind === 'build_line' && i.target) move = { id: 'ask', kind: 'build_line', target: i.target, cost: 0, ...(i.opt ? { opt: i.opt } : {}) };
      else {
        const c = buildCandidates(s, { player: me, max: 20 }).find((v) => v.cand.kind === 'build_line' && v.nodes.includes(at));
        move = c ? c.cand : null;
      }
    }
    const name = nodeById(s, at)?.name ?? 'der';
    if (!move) {
      this.reply(`Ikke nu. Jeg kan ikke se en ledning fra ${name}, der kan bygges.`, 'worried');
      return true;
    }
    const label = labelOf(s, move);
    const r = game.pushAs(me, move.kind, move.target, move.opt, 'bot');
    if (r.ok) this.reply(`Jeg tager den: ${label.charAt(0).toLowerCase()}${label.slice(1)}.`, 'happy');
    else this.reply(`Ikke nu, fordi ${WHY_NOT[r.error] ?? 'det ikke kan lade sig gøre lige nu'}.`, 'worried');
    return true;
  }

  private reply(line: string, mood: Parameters<VoltPanel['speak']>[1]): void {
    this.panel.speak(line, mood, performance.now());
    this.deps.toast.show(`Volt: ${line}`, mood === 'happy' ? 'good' : 'info', 2600);
  }

  stats(): VoltStats | null {
    return this.controller?.stats() ?? null;
  }

  /** Logbogen på resultatskærmen, lige over knapperne. */
  logbook(screen: HTMLElement): void {
    const s = this.stats();
    if (!s) return;
    const card = screen.querySelector<HTMLElement>('.card.result');
    if (!card) return;
    const el = logbookEl(this.faceUrl, s, this.moments);
    this.book = el;
    const actions = card.querySelector('.actions');
    if (actions) card.insertBefore(el, actions);
    else card.append(el);
  }

  /** Plads, Volts panel tager fra kortet (px): i venstre side på store skærme, nederst på telefonen. */
  inset(rail: boolean): { left: number; bottom: number } {
    if (!this.mode) return { left: 0, bottom: 0 };
    if (rail) return { left: window.innerHeight >= 560 ? 300 : 0, bottom: 0 };
    return { left: 0, bottom: 96 };
  }
}
